/**
 * OCIF seeds five org unit types BY NAME (SeedOcifScopeOrgUnitTypeService, from the backend's
 * OcifScopeOrgUnitType enum), and the seeder, OrgUnitCodeValidator and dashboard RLS all match those
 * names. Renaming one makes the seeder create a duplicate on the next startup, and the seeder rewrites
 * their ParentRestrictions on every startup. This module is the only place in the frontend that
 * names them.
 */
export const SYSTEM_ORG_UNIT_TYPES = {
  root: 'OCIF',
  sector: 'Sector',
  category: 'Categoría',
  subCategory: 'Sub-Categoría',
  institution: 'Institución',
} as const;

const SYSTEM_NAMES: string[] = Object.values(SYSTEM_ORG_UNIT_TYPES);

export type ParentRestriction = { orgUnitType: number };

export type OrgUnitTypeRecord = {
  id?: number;
  name: string;
  parentRestrictions: ParentRestriction[];
  removed?: boolean;
  [key: string]: unknown;
};

export type CodeBehaviour = 'typed' | 'derived' | 'none';

export type TypeBehaviour = { code: CodeBehaviour; isInstitution: boolean };

export function isSystemType(name: string | null | undefined): boolean {
  return !!name && SYSTEM_NAMES.includes(name);
}

/** Which business-code field a type gets, and whether it carries the institution sections. */
export function typeBehaviour(name: string | null | undefined): TypeBehaviour {
  if (name === SYSTEM_ORG_UNIT_TYPES.sector) return { code: 'typed', isInstitution: false };
  if (name === SYSTEM_ORG_UNIT_TYPES.category || name === SYSTEM_ORG_UNIT_TYPES.subCategory) return { code: 'derived', isInstitution: false };
  return { code: 'none', isInstitution: name === SYSTEM_ORG_UNIT_TYPES.institution };
}

/** The backend's OrgUnitCodeGenerator.Segment: first three characters plus the last one, upper-cased. */
export function codeSegment(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';
  return (trimmed.substring(0, 3) + trimmed.substring(trimmed.length - 1)).toUpperCase();
}

/**
 * The code a Categoría / Sub-Categoría must carry: its parent's code followed by its name's segment
 * (OrgUnitCodeGenerator.Chain). The backend rejects any other value. '' when either half is missing.
 */
export function deriveGroupingCode(parentCode: string | null | undefined, name: string | null | undefined): string {
  const parent = (parentCode ?? '').trim();
  const segment = codeSegment(name);
  return parent && segment ? parent + segment : '';
}

/** Type ids a unit of this type may sit under. Empty means top level: no parent. */
export function allowedParentTypeIds(type: { parentRestrictions?: ParentRestriction[] | null } | null | undefined): number[] {
  return (type?.parentRestrictions ?? []).map(restriction => Number(restriction.orgUnitType)).filter(id => Number.isFinite(id));
}

export function allowedParentTypeNames(type: { parentRestrictions?: ParentRestriction[] | null } | null | undefined, types: OrgUnitTypeRecord[]): string[] {
  return allowedParentTypeIds(type)
    .map(id => types.find(candidate => candidate.id === id)?.name)
    .filter((name): name is string => !!name);
}

export function toParentRestrictions(ids: number[]): ParentRestriction[] {
  return Array.from(new Set(ids))
    .sort((a, b) => a - b)
    .map(orgUnitType => ({ orgUnitType }));
}

export function findTypeByName(types: OrgUnitTypeRecord[], name: string): OrgUnitTypeRecord | undefined {
  return types.find(type => type.name === name);
}

/** Restrictions sorted, so toggling a parent off and on again compares equal to what was loaded. */
export function normalizeOrgUnitType(raw: any): OrgUnitTypeRecord {
  return {
    ...raw,
    id: raw?.id == null ? undefined : Number(raw.id),
    name: String(raw?.name ?? ''),
    parentRestrictions: toParentRestrictions(allowedParentTypeIds(raw)),
    removed: !!raw?.removed,
  };
}

export function newOrgUnitTypeStub(): OrgUnitTypeRecord {
  return { name: '', parentRestrictions: [] };
}
